import React, { useCallback, useContext, useRef, useState } from 'react'
import Dropzone, { DropzoneRef } from 'react-dropzone'

import { RefetchQueriesFunction } from '@apollo/react-common'
import { bemJoin } from 'bem-join'
import { Alert, Align, Button, Heading, IMessage, Margin, Popup } from 'component-library-next'

import Loader from '@app/common/Components/Loader'
import { LawyerDocument, ProfessionalAuditReport } from '@app/graphql/types'
import { ModernChat } from '@app/lib/components/ModernChat'
import { DocumentsList } from '@app/lib/display/DocumentsList/DocumentsList'
import { AuditReportView } from '@app/modules/Messages/Components/Answer/AuditReportView/AuditReportView'
import { ConfirmDocumentDetachPopUp } from '@app/modules/Messages/Components/Answer/ConfirmDocumentDetachPopUp'
import { ConfirmMessageDeletePopUp } from '@app/modules/Messages/Components/Answer/ConfirmMessageDeletePopUp'
import { useDeleteLawyerMessage } from '@app/modules/Messages/Components/Answer/useDeleteLawyerMessage'
import { useDetachLawyerDocument } from '@app/modules/Messages/Components/Answer/useDetachLawyerDocument'
import { useUploadDocument } from '@app/modules/Messages/Components/Answer/useUploadDocument'
import { OpenAllDocuments } from '@app/modules/Messages/Components/OpenAllDocuments/OpenAllDocuments'
import { useCustomerId } from '@app/modules/Messages/hooks/useCustomerId'
import { letterRequestContext } from '@app/modules/Messages/letterRequestContext'
import { useCustomerAsLawyerQuery } from '@app/queries/CustomerAsLawyer/CustomerAsLawyer.queries'
import { Message } from '@app/queries/CustomerAsLawyer/models'

import './Answer.scss'

type Props = {
  isLastMessageQuestion: boolean
  message: Message
}

export const Answer = ({ isLastMessageQuestion, message }: Props) => {
  const { documents } = message
  const { letterRequestData, refetchLetter } = useContext(letterRequestContext)
  const customerId = useCustomerId()
  const {
    data,
    loading,
    refetch: refetchMessages
  } = useCustomerAsLawyerQuery({
    variables: { customerId },
    fetchPolicy: 'cache-first'
  })

  const b = bemJoin('dialog-item__lawyer')

  const latestLawyerMessage = data?.customer.latestAnswer
  const isLatestLawyerMessage = latestLawyerMessage?.id === message.id

  const dropzoneRef = useRef<DropzoneRef>(null)

  const [uploadingDocument, setUploadingDocument] = useState(false)
  const [messageIdToDelete, setMessageIdToDelete] = useState<undefined | number>(undefined)
  const [documentIdToDetach, setDocumentIdToDetach] = useState<undefined | number>(undefined)
  const [showIsLetterPopup, setShowIsLetterPopup] = useState(false)
  const [fileToUpload, setFileToUpload] = useState<File[]>([])

  const uploadDocument = useUploadDocument({
    setUploadingDocument,
    refetchMessages,
    messageId: message.id
  })
  const uploadLetter = useUploadDocument({
    setUploadingDocument,
    refetchMessages,
    messageId: message.id,
    letterRequestId: letterRequestData?.openLetterRequest?.letterRequestId,
    refetchLetter
  })

  const handleUpload = async (file: File[]) => {
    if (letterRequestData?.openLetterRequest) {
      setShowIsLetterPopup(true)
      setFileToUpload(file)
    } else {
      await uploadDocument(file)
      setShowIsLetterPopup(false)
    }
  }

  const handleUploadDocument = async () => {
    await uploadDocument(fileToUpload)
    setShowIsLetterPopup(false)
  }

  const handleUploadLetter = async () => {
    await uploadLetter(fileToUpload)
    setShowIsLetterPopup(false)
  }

  const openFileChooser = useCallback(() => {
    dropzoneRef.current?.open()
  }, [dropzoneRef])

  const { deleteMessage, deleteMessageLoading, deleteMessageError } = useDeleteLawyerMessage({
    refetchMessages,
    refetchLetter: refetchLetter as unknown as RefetchQueriesFunction
  })
  const { detachDocument, detachDocumentLoading, detachDocumentError } = useDetachLawyerDocument({
    messageId: message.id,
    refetchMessages,
    refetchLetter: refetchLetter as unknown as RefetchQueriesFunction
  })

  const showLoader = uploadingDocument || deleteMessageLoading || detachDocumentLoading || loading
  const showError = deleteMessageError || detachDocumentError

  const messageDocumentsIds = (message.documents || []).map((document) => Number(document.id))
  const answerIncludesLawyerLetter = letterRequestData?.letterDocumentIds?.some((letterId) =>
    messageDocumentsIds.includes(letterId)
  )

  const messageWithoutDocuments = {
    ...message,
    documents: []
  }

  return (
    <div className='dialog-item__lawyer'>
      {showLoader && <Loader />}
      {showError && (
        <Alert color={Alert.colors.warning}>
          Leider ist der Vorgang fehlgeschlagen. Bitte versuche es noch einmal
        </Alert>
      )}
      {documents && documentIdToDetach && (
        <ConfirmDocumentDetachPopUp
          setDocumentIdToDetach={setDocumentIdToDetach}
          documentIdToDetach={documentIdToDetach}
          documents={documents}
          messageIdToDelete={messageIdToDelete}
          detachDocument={detachDocument}
        />
      )}
      {messageIdToDelete && (
        <ConfirmMessageDeletePopUp
          setMessageIdToDelete={setMessageIdToDelete}
          messageIdToDelete={messageIdToDelete}
          deleteMessage={deleteMessage}
        />
      )}

      <ModernChat
        collapsesLongText={true}
        deleteMessage={(messageId) => setMessageIdToDelete(messageId)}
        displayLetterRequest={false}
        displayLetterAttached={answerIncludesLawyerLetter}
        message={messageWithoutDocuments as unknown as IMessage}
        recommendedTemplateName={message.messageHasTemplate?.emailTemplate?.name}
      >
        {message?.auditReport?.report && (
          <AuditReportView
            topicName={message.topic.name}
            report={message.auditReport.report as unknown as ProfessionalAuditReport}
          />
        )}
        {documents && documents.length > 1 && (
          <div className={b('open-all-documents')}>
            <OpenAllDocuments documents={documents} />
          </div>
        )}
        {documents && (
          <DocumentsList
            documents={documents as LawyerDocument[]}
            onRemove={(documentId: number) => setDocumentIdToDetach(documentId)}
            onAddDocument={
              isLatestLawyerMessage && isLastMessageQuestion ? openFileChooser : undefined
            }
          />
        )}
      </ModernChat>
      <Dropzone
        ref={dropzoneRef}
        noClick={true}
        accept={{
          'application/pdf': ['.pdf'],
          'application/msword': ['.doc'],
          'application/vnd.openxmlformats': ['.docx'],
          'image/jpeg': ['.jpg', '.jpeg'],
          'image/png': ['.png']
        }}
        onDrop={handleUpload}
      >
        {({ getRootProps, getInputProps }) => (
          <div {...getRootProps()}>
            <input {...getInputProps()} />
          </div>
        )}
      </Dropzone>
      {showIsLetterPopup && (
        <Popup onCloseButtonClick={() => setShowIsLetterPopup(false)}>
          <Heading size={Heading.sizes.small} color={Heading.colors.black}>
            Ist das beigefügte Dokument eine Kopie Ihres Anwaltsbriefs?
          </Heading>
          <Align alignItem={Align.alignItemDirections.center} direction={Align.directions.center}>
            <Margin>
              <Button onClick={handleUploadLetter}>ja</Button>
            </Margin>
            <Margin>
              <Button onClick={handleUploadDocument} color={Button.colors.minor}>
                nein
              </Button>
            </Margin>
          </Align>
        </Popup>
      )}
    </div>
  )
}
